import { useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, KeyRound, ListMusic, Loader2, LogOut, Pencil, Trash2, Upload, Users } from "lucide-react";
import { ownerApi, apiErrorText } from "@/lib/ownerApi";
import SongEditForm from "@/components/site/SongEditForm";
import ChangePasswordDialog from "@/components/site/ChangePasswordDialog";

export default function OwnerMusicControls({ songs, currentId, onSongsChange, onSignOut }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);

  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    let list = songs;
    let added = 0;
    for (const [i, file] of files.entries()) {
      setBusy(files.length > 1 ? `Adding ${i + 1}/${files.length}` : "Adding");
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await ownerApi.post("/admin/music", form);
        list = [...list, data];
        onSongsChange(list);
        added += 1;
      } catch (err) {
        toast.error(`${file.name}: ${apiErrorText(err)}`);
      }
    }
    setBusy("");
    if (added) {
      toast.success(added === 1 ? "Song added to your playlist." : `${added} songs added to your playlist.`);
      setOpen(true);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove "${s.title}" from your playlist?`)) return;
    try {
      await ownerApi.delete(`/admin/music/${s.id}`);
      onSongsChange(songs.filter((x) => x.id !== s.id));
    } catch (err) {
      toast.error(apiErrorText(err));
    }
  };

  const move = async (i, dir) => {
    const ids = songs.map((s) => s.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    try {
      const { data } = await ownerApi.put("/admin/music/order", { ids });
      onSongsChange(data.songs);
    } catch (err) {
      toast.error(apiErrorText(err));
    }
  };

  const signOut = async () => {
    await ownerApi.post("/auth/logout").catch(() => {});
    onSignOut();
  };

  return (
    <div data-testid="owner-music-controls" className="mb-2 w-[min(340px,calc(100vw-2rem))] rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[rgba(14,13,11,0.92)] backdrop-blur-md p-2">
      <div className="flex flex-nowrap items-center gap-1">
        <input ref={inputRef} data-testid="owner-music-file-input" type="file" multiple accept="audio/*,.mp3,.m4a,.aac,.wav,.ogg" className="hidden" onChange={upload} />
        <button data-testid="owner-upload-song-button" disabled={!!busy} onClick={() => inputRef.current?.click()} className="btn-primary text-xs py-2 px-3 whitespace-nowrap disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {busy || "Add songs"}
        </button>
        <button data-testid="owner-playlist-toggle" onClick={() => setOpen((o) => !o)} className="text-xs text-[#F5F0E8] hover:text-[#D4AF37] inline-flex items-center gap-1 px-2 py-2 whitespace-nowrap">
          <ListMusic size={13} /> Playlist ({songs.length}) {open ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
        </button>
        <a href="/admin" data-testid="owner-families-link" aria-label="Families" title="Families" className="ml-auto text-[#A0988E] hover:text-[#F5F0E8] inline-flex items-center p-2">
          <Users size={14} />
        </a>
        <button data-testid="owner-change-password-button" onClick={() => setPwOpen(true)} aria-label="Change password" title="Change password" className="text-[#A0988E] hover:text-[#F5F0E8] inline-flex items-center p-2">
          <KeyRound size={14} />
        </button>
        <button data-testid="owner-sign-out-button" onClick={signOut} aria-label="Sign out" title="Sign out" className="text-[#A0988E] hover:text-[#F5F0E8] inline-flex items-center p-2">
          <LogOut size={14} />
        </button>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      {open && (
        <ol data-testid="owner-playlist" className="mt-2 max-h-56 overflow-y-auto space-y-1 border-t border-[rgba(197,165,90,0.15)] pt-2">
          {songs.length === 0 && <li className="text-xs text-[#A0988E] px-2 py-2">No songs yet. Add a few to start your playlist.</li>}
          {songs.map((s, i) => (
            <li key={s.id} data-testid={`owner-playlist-item-${i}`} className={`flex items-center gap-1 rounded-lg px-2 py-1.5 ${s.id === currentId ? "bg-[rgba(212,175,55,0.1)]" : ""}`}>
              <span className="text-[11px] text-[#A0988E] w-5 self-start pt-1">{i + 1}.</span>
              {editing === s.id ? (
                <SongEditForm song={s} index={i} onCancel={() => setEditing(null)}
                              onSaved={(updated) => { onSongsChange(songs.map((x) => (x.id === updated.id ? updated : x))); setEditing(null); }} />
              ) : (
                <>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-[#F5F0E8] truncate" title={s.title}>{s.title}</span>
                    <span data-testid={`owner-song-artist-${i}`} className="block text-[11px] text-[#A0988E] truncate">{s.artist || "No artist added"}</span>
                  </span>
                  <button data-testid={`owner-song-edit-${i}`} onClick={() => setEditing(s.id)} aria-label="Edit song details" className="p-1 text-[#A0988E] hover:text-[#D4AF37]"><Pencil size={13} /></button>
              <button data-testid={`owner-song-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" className="p-1 text-[#A0988E] hover:text-[#D4AF37] disabled:opacity-25"><ChevronUp size={14} /></button>
              <button data-testid={`owner-song-down-${i}`} disabled={i === songs.length - 1} onClick={() => move(i, 1)} aria-label="Move down" className="p-1 text-[#A0988E] hover:text-[#D4AF37] disabled:opacity-25"><ChevronDown size={14} /></button>
              <button data-testid={`owner-song-remove-${i}`} onClick={() => remove(s)} aria-label="Remove song" className="p-1 text-[#A0988E] hover:text-[#E6B89C]"><Trash2 size={14} /></button>
                </>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
